/**
 * GuestLayout Component
 * 
 * Simple modal layout for guest users editing items.
 * Only allows editing name and description.
 * Minimal UI with save/cancel buttons.
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { EditableItem } from '../types';
import { ItemForm } from '../ItemForm';

export interface GuestLayoutProps {
  item: EditableItem;
  onChange: (patch: Partial<EditableItem>) => void;
  onSave: () => Promise<void>;
  onClose: () => void;
  saving?: boolean;
}

export const GuestLayout: React.FC<GuestLayoutProps> = ({
  item,
  onChange,
  onSave,
  onClose,
  saving = false,
}) => {
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!item.name.trim()) {
      alert('Item name is required');
      return;
    }

    await onSave();
  };

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-text-inverse)',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div
            style={{
              padding: '20px',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>
              {item.id === -1 ? 'Add Item' : 'Edit Item'}
            </h2>
          </div>

          {/* Body */}
          <div style={{ padding: '20px' }}>
            <ItemForm
              item={item}
              onChange={onChange}
              mode="guest"
            />
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 20px',
              borderTop: '1px solid var(--color-border)',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: 'transparent',
                color: 'var(--color-text-secondary)',
                fontSize: '14px',
                fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !item.name.trim()}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--color-accent-secondary)',
                color: 'var(--color-text-inverse)',
                fontSize: '14px',
                fontWeight: 600,
                cursor: saving || !item.name.trim() ? 'not-allowed' : 'pointer',
                opacity: saving || !item.name.trim() ? 0.6 : 1,
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default GuestLayout;
